import { AfterViewInit, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { combineLatest, EMPTY, Observable, startWith, Subscription } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { LanguageService } from '@fcom/ui-translate';
import { symbolToLangPath, unsubscribe } from '@fcom/core/utils';
import { AppState, ConfigService, DdsLanguage } from '@fcom/core';
import { CmsContent, CMSelectionRules, CmsTarget, CmsTemplate } from '@fcom/core-api';
import { LoyaltyRoutesPath } from '@fcom/loyalty-core/interfaces';
import { CmsTemplateService } from '@fcom/cms/services';
import { FragmentService, PageMetaService } from '@fcom/common/services';
import { setPageMetadata } from '@fcom/loyalty-core';
import { Profile, ProfileTier } from '@fcom/core-api/login';
import { profile } from '@fcom/core/selectors';
import { finShare, safeMap } from '@fcom/rx';
import { NotificationTheme } from '@fcom/ui-components';
import { BenefitItemKeyEnum, BenefitItemV2 } from '@fcom/core/interfaces/tier-benefit.interface';
import { TierBenefitService } from '@fcom/core/services/tier-benefit/tier-benefit.service';
import { TIER_DATA_MAP } from '@fcom/common';
import { CreditCardPersonalizationParameter } from '@fcom/core-api/interfaces';

import { Item } from '../../components/tier-benefits/tier-benefits.component';
import { Status, TransactionService } from '../../services/transaction.service';
import { Voucher } from '../../interfaces';
import { UpgradeBenefitsState, VoucherService } from '../../services/voucher.service';

@Component({
  templateUrl: 'plus-dashboard.component.html',
  styleUrls: ['../../../loyalty-core/assets/styles/common.scss', './plus-dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class PlusDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly TIER_DATA_MAP = TIER_DATA_MAP;
  readonly NotificationTheme = NotificationTheme;
  readonly ProfileTier = ProfileTier;

  HIDE_BENEFITS = [BenefitItemKeyEnum.SnackDrink, BenefitItemKeyEnum.EasyBookings];

  welcomeTextWithLink$: Observable<string>;
  template$: Observable<CmsTemplate>;
  pageTitle$: Observable<string>;
  sideBarContent$: Observable<CmsContent>;
  creditCardBannerParams$: Observable<{ [key: string]: string }>;
  bannerCta$: Observable<CmsTarget>;
  profile$: Observable<Profile>;
  transactionsAndBenefitsLoading$: Observable<boolean>;
  vouchers$: Observable<Voucher[]>;

  tierBenefits$: Observable<Item[]>;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private languageService: LanguageService,
    private cmsTemplateService: CmsTemplateService,
    private pageMetaService: PageMetaService,
    private tierBenefitService: TierBenefitService,
    private store$: Store<AppState>,
    private fragmentService: FragmentService,
    private router: Router,
    private transactionService: TransactionService,
    private voucherService: VoucherService,
    private configService: ConfigService
  ) {}

  ngOnInit(): void {
    this.vouchers$ = this.voucherService.getVouchers().pipe(
      catchError(() => EMPTY),
      finShare(),
      startWith([])
    );

    this.transactionsAndBenefitsLoading$ = combineLatest([
      this.transactionService.status$,
      this.voucherService.status$,
    ]).pipe(
      map(
        ([transactionsStatus, vouchersStatus]) =>
          (this.transactionService.transactionsStore.length === 0 && transactionsStatus === Status.LOADING) ||
          vouchersStatus === UpgradeBenefitsState.LOADING
      )
    );

    this.profile$ = this.store$.pipe(profile(), take(1));

    const ddsLocale: DdsLanguage = this.languageService.ddsLocaleValue;

    this.creditCardBannerParams$ = this.profile$.pipe(
      take(1),
      safeMap((profile: Profile) => ({
        [CreditCardPersonalizationParameter.AKTIA_CREDIT_CARD]: profile.hasAktiaVisaCreditCard ? 'true' : 'false',
        ...(this.configService.cfg.enableVisaLightCreditCardBanner && {
          [CreditCardPersonalizationParameter.AKTIA_LIGHT_CREDIT_CARD]: profile.hasAktiaVisaLightCreditCard
            ? 'true'
            : 'false',
        }),
        tier: profile.tier,
      })),
      finShare()
    );

    const articles$ = this.profile$.pipe(
      take(1),
      switchMap(({ tier }) =>
        this.fragmentService.getFragment<CMSelectionRules>('LOYALTY.fragments.tierBenefits.url', {
          tier,
        })
      ),
      safeMap(({ items }) => items[0].items)
    );
    this.sideBarContent$ = articles$.pipe(safeMap((articles) => articles[0]));
    this.bannerCta$ = articles$.pipe(safeMap((articles) => this.articleToTarget(articles[1])));

    const lang = this.languageService.langKeyValue;
    this.welcomeTextWithLink$ = this.languageService
      .translate('pricesStartingFromOfferSuffix')
      .pipe(map((text) => symbolToLangPath(text, ddsLocale)));

    this.template$ = this.cmsTemplateService.load(`/${this.languageService.langValue}/${LoyaltyRoutesPath.PLUS}`);
    this.pageTitle$ = this.template$.pipe(map((template: CmsTemplate) => template?.header?.[0]?.title ?? ''));

    this.profile$ = this.store$.pipe(profile(), finShare());

    this.tierBenefits$ = this.profile$.pipe(
      take(1),
      map(({ tier }) => TIER_DATA_MAP[tier].tierName.toUpperCase()),
      switchMap((tier) => this.tierBenefitService.getBenefitJson(tier, lang).pipe(map((benefits) => benefits[tier]))),
      map((benefits: BenefitItemV2[]) =>
        this.tierBenefitService
          .filterBenefits(benefits)
          .filter((row) => row?.icon?.name)
          .filter((row) => !this.HIDE_BENEFITS.includes(row.benefitItemKey))
          .map((row) => ({
            icon: row?.icon,
            title: row?.title,
            text: row?.description?.generalDescription,
            tags: row?.displayTags.toLowerCase().split(' ').filter(Boolean),
            changed: row?.displayTags === 'Changed',
          }))
      ),
      startWith([])
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      this.template$.subscribe((template: CmsTemplate) => setPageMetadata(template, this.pageMetaService))
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  goUsePoints(): void {
    this.router.navigateByUrl(
      `/${this.languageService.langValue}/${LoyaltyRoutesPath.PLUS}/${LoyaltyRoutesPath.BALANCE_AND_TRANSACTIONS}#usePoints`,
      { replaceUrl: true }
    );
  }

  private articleToTarget(article: CmsContent): CmsTarget {
    return {
      ...article.target,
      title: article.teaserTitle,
    };
  }
}
